import { Camera, Loader2, X } from 'lucide-react'
import { useRef, useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useDepartments,
  type FlatDepartment,
  useCreateEmployee,
  useUpdateEmployee,
  useActiveEmployees,
  useUploadAvatar,
  useEmployeeAvatar,
  type Employee,
  type CreateEmployeeInput,
  type UpdateEmployeeInput,
  type CompanyRole,
  usePositions,
  type FlatPosition,
} from '@/hooks'
import { processFileForUpload, isImageFile } from '@/lib/image-utils'
import { cn } from '@/lib/utils'

interface EmployeeFormProps {
  employee?: Employee
  onClose: () => void
  onSuccess?: () => void
}

const ROLE_OPTIONS: { value: CompanyRole; label: string }[] = [
  { value: 'director', label: '主管' },
  { value: 'finance', label: '财务' },
  { value: 'hr', label: '人事' },
  { value: 'admin', label: '行政' },
  { value: 'pm', label: '项目经理' },
  { value: 'developer', label: '开发人员' },
]

export function EmployeeForm({ employee, onClose, onSuccess }: EmployeeFormProps) {
  const isEdit = !!employee
  const createMutation = useCreateEmployee()
  const updateMutation = useUpdateEmployee()
  const uploadAvatarMutation = useUploadAvatar()

  const { data: departments } = useDepartments()
  const { data: positions } = usePositions()
  const { data: activeEmployees } = useActiveEmployees()
  const { data: avatarData } = useEmployeeAvatar(employee?.id ?? '')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)

  const [formData, setFormData] = useState({
    name: employee?.name ?? '',
    email: employee?.email ?? '',
    phone: employee?.phone ?? '',
    role: employee?.role ?? 'developer',
    departmentId: employee?.departmentId ?? '',
    positionId: employee?.positionId ?? '',
    managerId: employee?.managerId ?? '',
    hireDate: employee?.hireDate?.split('T')[0] ?? new Date().toISOString().split('T')[0],
    resignDate: employee?.resignDate?.split('T')[0] ?? '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 处理头像选择
  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) { return }

    if (!isImageFile(file)) {
      setErrors({ ...errors, avatar: '请选择图片文件' })
      return
    }

    // 转换为 WebP
    try {
      const processed = await processFileForUpload(file)
      const processedFile = new File([processed.blob], processed.fileName, { type: processed.blob.type })
      setAvatarFile(processedFile)
      setAvatarPreview(URL.createObjectURL(processed.blob))
      setErrors({ ...errors, avatar: '' })
    } catch {
      setErrors({ ...errors, avatar: '图片处理失败' })
    }
  }

  // 获取当前头像 URL
  const currentAvatarUrl = avatarPreview || avatarData?.url || null

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) { newErrors.name = '请输入姓名' }
    if (!formData.email.trim()) { newErrors.email = '请输入邮箱' }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { newErrors.email = '邮箱格式不正确' }
    if (!formData.hireDate) { newErrors.hireDate = '请选择入职日期' }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) { return }

    if (isEdit) {
      const data: UpdateEmployeeInput = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        role: formData.role,
        departmentId: formData.departmentId || undefined,
        positionId: formData.positionId || undefined,
        managerId: formData.managerId || undefined,
        hireDate: formData.hireDate,
        resignDate: formData.resignDate || undefined,
      }
      updateMutation.mutate(
        { id: employee.id, data },
        {
          onSuccess: () => {
            // 上传头像（如果有新选择的）
            if (avatarFile) {
              setAvatarUploading(true)
              void (async () => {
                try {
                  await uploadAvatarMutation.mutateAsync({ id: employee.id, file: avatarFile })
                } catch {
                  // 头像上传失败不阻止表单提交
                } finally {
                  setAvatarUploading(false)
                  onSuccess?.()
                  onClose()
                }
              })()
            } else {
              onSuccess?.()
              onClose()
            }
          }
        }
      )
    } else {
      const data: CreateEmployeeInput = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        role: formData.role,
        departmentId: formData.departmentId || undefined,
        positionId: formData.positionId || undefined,
        managerId: formData.managerId || undefined,
        hireDate: formData.hireDate,
      }
      createMutation.mutate(data, {
        onSuccess: (newEmployee) => {
          // 上传头像（如果有选择的）
          if (avatarFile && newEmployee?.id) {
            setAvatarUploading(true)
            void (async () => {
              try {
                await uploadAvatarMutation.mutateAsync({ id: newEmployee.id, file: avatarFile })
              } catch {
                // 头像上传失败不阻止表单提交
              } finally {
                setAvatarUploading(false)
                onSuccess?.()
                onClose()
              }
            })()
          } else {
            onSuccess?.()
            onClose()
          }
        }
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending || avatarUploading
  const mutationError = createMutation.error || updateMutation.error

  // 过滤掉当前员工作为上级选项
  const managerOptions = activeEmployees?.filter((e) => e.id !== employee?.id) ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">{isEdit ? '编辑员工' : '新建员工'}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="p-6">
          <div className="space-y-4">
            {/* 头像 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">头像</label>
              <div className="flex items-center gap-4">
                <div
                  className="relative h-16 w-16 cursor-pointer overflow-hidden rounded-full bg-muted"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {currentAvatarUrl ? (
                    <img
                      src={currentAvatarUrl}
                      alt="头像"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xl font-semibold text-muted-foreground">
                      {formData.name.charAt(0) || '?'}
                    </div>
                  )}
                  {avatarUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="h-5 w-5 animate-spin text-white" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity hover:opacity-100">
                    <Camera className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  <p>点击更换头像</p>
                  <p>支持 JPG、PNG，自动转换为 WebP</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void handleAvatarSelect(e)}
                />
              </div>
              {errors.avatar && <p className="mt-1 text-sm text-destructive">{errors.avatar}</p>}
            </div>

            {/* 姓名 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                姓名 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入姓名"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.name && 'border-destructive'
                )}
              />
              {errors.name && <p className="mt-1 text-sm text-destructive">{errors.name}</p>}
            </div>

            {/* 邮箱 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                邮箱 <span className="text-destructive">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="请输入邮箱"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.email && 'border-destructive'
                )}
              />
              {errors.email && <p className="mt-1 text-sm text-destructive">{errors.email}</p>}
            </div>

            {/* 电话 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">电话</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="请输入电话"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* 角色 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">角色</label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value as CompanyRole })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择角色" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 部门 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">部门</label>
              <Select
                value={formData.departmentId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, departmentId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择部门" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">请选择部门</SelectItem>
                  {departments?.map((dept: FlatDepartment) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 职位 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">职位</label>
              <Select
                value={formData.positionId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, positionId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择职位" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">请选择职位</SelectItem>
                  {positions?.map((pos: FlatPosition) => (
                    <SelectItem key={pos.id} value={pos.id}>{pos.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 上级 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">直属上级</label>
              <Select
                value={formData.managerId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, managerId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择上级" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">请选择上级</SelectItem>
                  {managerOptions.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 入职日期 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                入职日期 <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                value={formData.hireDate}
                onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.hireDate && 'border-destructive'
                )}
              />
              {errors.hireDate && <p className="mt-1 text-sm text-destructive">{errors.hireDate}</p>}
            </div>

            {/* 离职日期（仅编辑时显示） */}
            {isEdit && (
              <div>
                <label className="mb-1.5 block text-sm font-medium">离职日期</label>
                <input
                  type="date"
                  value={formData.resignDate}
                  onChange={(e) => setFormData({ ...formData, resignDate: e.target.value })}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            {mutationError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {mutationError.message}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted">
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : isEdit ? '保存' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
